'use client';

import React, { useMemo } from 'react';
import { Typography } from 'src/components/PulseUI';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  buildRoleDisplayGroups,
  createPermissionCode,
  getActionDisplayText,
} from '../../utils';

const slugify = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, '-');

const PermissionIcon = ({
  granted,
}: {
  granted: boolean;
}): React.ReactElement =>
  granted ? (
    <span
      className="inline-flex items-center justify-center w-4 h-4 text-green-500 bg-green-50 rounded-full"
      data-testid="permission-granted-icon"
    >
      <Icon icon={faCheck} className="text-xs" />
    </span>
  ) : (
    <span
      className="inline-flex items-center justify-center w-4 h-4 text-neutral-100"
      data-testid="permission-denied-icon"
    >
      <Icon icon={faCircle} className="text-xs" />
    </span>
  );

export interface PermissionMatrixProps {
  /**
   * Permission codes (`action.resource.scope`) that define the matrix
   * structure — every group/row/column shown is derived from these.
   */
  permissions: string[];
  /**
   * Subset of codes to mark as granted (green check); the rest render as a
   * denied (empty) cell. Defaults to `permissions`, i.e. everything shown is
   * granted. Pass the full permission catalog as `permissions` and a role's
   * codes here to show all permissions with only the held ones checked.
   */
  grantedPermissions?: string[];
  /**
   * Map of permission code -> human description (from the catalog), used for
   * each row's helper text. Rows fall back to a generated description when a
   * code is absent here.
   */
  descriptions?: Record<string, string>;
}

/**
 * Read-only matrix of permission codes. Sections are derived dynamically from
 * the codes (see `buildRoleDisplayGroups`) so every resource surfaces, with
 * columns limited to the actions present in the structure.
 *
 * Shared by RoleDisplay (the full catalog, checked against a company role's
 * permissions) and UserRolesAdmin (the combined permissions of a user's
 * selected roles, where structure and granted set are the same).
 */
const PermissionMatrix = ({
  permissions,
  grantedPermissions,
  descriptions,
}: PermissionMatrixProps): React.ReactElement => {
  const permissionLookup = useMemo(
    () => new Set(grantedPermissions ?? permissions),
    [grantedPermissions, permissions]
  );

  const permissionGroups = useMemo(
    () => buildRoleDisplayGroups(permissions, descriptions),
    [permissions, descriptions]
  );

  return (
    <div
      className="flex flex-col flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      data-testid="permission-groups"
    >
      {permissionGroups.map((group) => {
        const groupSlug = slugify(group.group);
        const isSingleAction = group.actions.length === 1;

        return (
          <div
            key={group.key}
            className="mb-6 border border-neutral-200 rounded-lg overflow-hidden"
          >
            {/* Group Title */}
            <div
              className="px-4 py-3 bg-neutral-50 border-b border-neutral-200"
              data-testid={`group-header-${groupSlug}`}
            >
              <div className="text-sm font-semibold text-neutral-700">
                {group.group}
              </div>
            </div>

            {/* Group-specific Header Row */}
            <div
              className="flex items-center px-4 py-2 bg-white border-b border-neutral-200"
              data-testid={`group-actions-header-${groupSlug}`}
            >
              <div className="w-64 text-sm font-semibold text-neutral-500">
                Resources
              </div>
              <div className="flex flex-1">
                {isSingleAction ? (
                  <div
                    key={group.actions[0]}
                    data-testid={`header-action-${groupSlug}-${group.actions[0]}`}
                    className="ml-auto text-xs font-medium text-neutral-500 pr-8"
                  >
                    {getActionDisplayText(group.actions[0])}
                  </div>
                ) : (
                  group.actions.map((action) => (
                    <div
                      key={action}
                      data-testid={`header-action-${groupSlug}-${action}`}
                      className="flex-1 flex justify-center text-xs font-medium text-neutral-500"
                    >
                      {getActionDisplayText(action)}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Group Permissions */}
            <div
              className="divide-y divide-neutral-100"
              data-testid={`group-permissions-${groupSlug}`}
            >
              {group.rows.map((row) => {
                const rowId = `${row.resource}-${row.scope}`;

                return (
                  <div
                    key={rowId}
                    data-testid={`permission-row-${rowId}`}
                    className="flex items-center px-4 py-3 hover:bg-neutral-50/80 transition-colors group"
                  >
                    <div className="flex flex-col w-64 gap-1">
                      <Typography className="text-sm font-semibold text-neutral-700 group-hover:text-neutral-900">
                        {row.label}
                      </Typography>
                      {row.subLabel && (
                        <Typography className="text-xs text-neutral-500 group-hover:text-neutral-600">
                          {row.subLabel}
                        </Typography>
                      )}
                    </div>
                    <div className="flex flex-1">
                      {isSingleAction ? (
                        <div
                          key={group.actions[0]}
                          data-testid={`permission-${rowId}-${group.actions[0]}`}
                          className="ml-auto flex items-center pr-8 py-1"
                          title={
                            descriptions?.[
                              createPermissionCode(
                                group.actions[0],
                                row.resource,
                                row.scope
                              )
                            ]
                          }
                        >
                          <PermissionIcon
                            granted={permissionLookup.has(
                              createPermissionCode(
                                group.actions[0],
                                row.resource,
                                row.scope
                              )
                            )}
                          />
                        </div>
                      ) : (
                        group.actions.map((action) => (
                          <div
                            key={action}
                            data-testid={`permission-${rowId}-${action}`}
                            className="flex-1 flex justify-center items-center rounded-md py-1"
                            title={
                              descriptions?.[
                                createPermissionCode(
                                  action,
                                  row.resource,
                                  row.scope
                                )
                              ]
                            }
                          >
                            <PermissionIcon
                              granted={permissionLookup.has(
                                createPermissionCode(
                                  action,
                                  row.resource,
                                  row.scope
                                )
                              )}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PermissionMatrix;
